import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

// Generic viewer for Expressions of Interest and Contact Enquiries.
export default function Submissions({ kind }) {
  const cfg = kind === "interest"
    ? { title: "Expressions of Interest", endpoint: "interest", statuses: ["New", "Under Review", "Contacted", "In Progress", "Completed", "Archived"], nameKey: "fullName" }
    : { title: "Contact Enquiries", endpoint: "contact", statuses: ["New", "Read", "Responded", "Resolved", "Archived"], nameKey: "name" };

  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => client.get(`/admin/${cfg.endpoint}`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const update = async (id, patch) => {
    const { data } = await client.patch(`/admin/${cfg.endpoint}/${id}`, patch);
    setItems((prev) => prev.map((i) => (i.id === id ? data : i)));
    if (selected?.id === id) setSelected(data);
    toast.success("Updated");
  };

  const filtered = items.filter((i) => {
    const matchQ = !q || JSON.stringify(i).toLowerCase().includes(q.toLowerCase());
    const matchF = filter === "all" || i.status === filter;
    return matchQ && matchF;
  });

  return (
    <AdminLayout title={cfg.title}>
      <div className="mb-5 flex flex-wrap gap-3">
        <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="submissions-search" />
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="submissions-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {cfg.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-gold/20 bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-gold/20 text-left text-xs uppercase tracking-wide text-ink-muted">
            <tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Received</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} className="border-b border-gold/10" data-testid={`row-${i.id}`}>
                <td className="p-3 text-maroon">{i[cfg.nameKey]}</td>
                <td className="p-3 text-ink-muted">{i.email}</td>
                <td className="p-3 text-ink-muted">{(i.createdAt || "").slice(0, 10)}</td>
                <td className="p-3"><span className="rounded-sm bg-ivory px-2 py-1 text-xs text-maroon">{i.status}</span></td>
                <td className="p-3 text-right"><button onClick={() => setSelected(i)} className="text-xs font-semibold text-maroon hover:underline" data-testid={`view-${i.id}`}>View</button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-ink-muted">No submissions found.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelected(null)}>
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-md bg-white p-7" onClick={(e) => e.stopPropagation()} data-testid="submission-detail">
            <h2 className="font-serif text-2xl text-maroon">{selected[cfg.nameKey]}</h2>
            <div className="mt-4 space-y-2 text-sm text-ink">
              <p><strong>Email:</strong> {selected.email}</p>
              {selected.mobileNumber && <p><strong>Mobile:</strong> {selected.mobileNumber}</p>}
              {selected.areaOfInterest && <p><strong>Area of Interest:</strong> {selected.areaOfInterest}</p>}
              {selected.subject && <p><strong>Subject:</strong> {selected.subject}</p>}
              <p><strong>Message:</strong> {selected.message}</p>
              <p className="text-xs text-ink-muted"><strong>Received:</strong> {selected.createdAt}</p>
              <p className="text-xs text-ink-muted"><strong>Email notification:</strong> {selected.emailNotificationStatus}</p>
            </div>
            <div className="mt-5">
              <label className="text-xs uppercase tracking-wide text-ink-muted">Status</label>
              <Select value={selected.status} onValueChange={(v) => update(selected.id, { status: v })}>
                <SelectTrigger className="mt-2" data-testid="detail-status"><SelectValue /></SelectTrigger>
                <SelectContent>{cfg.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="mt-4">
              <label className="text-xs uppercase tracking-wide text-ink-muted">Internal Notes (private)</label>
              <Textarea rows={3} defaultValue={selected.adminNotes} className="mt-2" data-testid="detail-notes"
                onBlur={(e) => e.target.value !== selected.adminNotes && update(selected.id, { adminNotes: e.target.value })} />
            </div>
            <button onClick={() => setSelected(null)} className="mt-6 w-full bg-maroon px-5 py-3 text-xs uppercase tracking-wide text-ivory">Close</button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
